/**
 * Query-string parsing for the diagnostic page.
 *
 * Deliberately hand-rolled rather than URLSearchParams: URLSearchParams silently REPAIRS
 * malformed input (it never reports a bad percent-escape, it turns "+" into a space with no
 * trace, and it hides the raw bytes behind an already-decoded view). Repairing the input is
 * exactly what we must not do here -- the raw bytes are the evidence.
 *
 * Contract: every exported function is total. It never throws, whatever it is handed --
 * including undefined, a bare "?", "%zz", lone surrogates, or a 100 KB value.
 */

/** A percent sign not followed by two hex digits -- decodeURIComponent throws on these. */
const INVALID_PERCENT = /%(?![0-9A-Fa-f]{2})/;

export interface DecodeResult {
  /** The decoded text, or the untouched raw text when decoding failed. */
  text: string;
  /** Human-readable reason decoding failed, or null when it succeeded. */
  error: string | null;
}

/**
 * decodeURIComponent, but total. "+" is first mapped to a space because query strings are
 * application/x-www-form-urlencoded -- callers are told about it via a note, since a literal
 * "+" the sender meant to keep would have arrived as "%2B".
 */
export function safeDecode(raw: string): DecodeResult {
  try {
    return { text: decodeURIComponent(raw.replace(/\+/g, "%20")), error: null };
  } catch {
    return { text: raw, error: describeDecodeFailure(raw) };
  }
}

/** Turn a URIError into something a human can act on, pointing at the offending bytes. */
function describeDecodeFailure(raw: string): string {
  const match = INVALID_PERCENT.exec(raw);
  if (match) {
    const snippet = raw.slice(match.index, match.index + 3);
    return `invalid percent-escape "${snippet}" at position ${match.index} (a % must be followed by two hex digits)`;
  }
  return "percent-escapes are individually well-formed but do not decode to valid UTF-8";
}

export interface ParsedParam {
  /** 1-based position among the "&"-separated segments, empty segments included. */
  position: number;
  /** The segment exactly as it appeared between separators. */
  rawSegment: string;
  rawKey: string;
  /** null when the segment contained no "=" at all (a valueless flag such as "?debug"). */
  rawValue: string | null;
  key: string;
  keyDecodeError: string | null;
  value: string | null;
  valueDecodeError: string | null;
  /** Anything noteworthy about this segment (empty segment, stray "=", "+" as space, ...). */
  notes: string[];
}

export interface ParsedQuery {
  /** The query string exactly as received, with any leading "?" removed. */
  raw: string;
  /** True when the URL carried a "?" at all -- "?" alone is different from no query string. */
  hadQuestionMark: boolean;
  /** True when there is nothing after the "?". */
  isEmpty: boolean;
  params: ParsedParam[];
  /** Keys (decoded) that appear more than once. Every occurrence is still listed in params. */
  duplicateKeys: string[];
  /** Observations about the query string as a whole. */
  notes: string[];
}

/**
 * Parse a query string into one row per "&"-separated segment.
 *
 * Empty segments (from "&&", or a leading/trailing "&") are KEPT as rows rather than dropped:
 * the whole point of this page is to show what actually arrived, and a stray "&" is precisely
 * the kind of thing the caller is hunting for.
 */
export function parseQueryString(input: string | null | undefined): ParsedQuery {
  const source = typeof input === "string" ? input : "";
  const hadQuestionMark = source.startsWith("?");
  const raw = hadQuestionMark ? source.slice(1) : source;

  const notes: string[] = [];
  if (raw.includes("#")) {
    notes.push('Contains a "#". Browsers never send the fragment to the server, so a "#" seen here arrived as literal text.');
  }
  if (raw.includes(";")) {
    notes.push('Contains a ";". Modern browsers and Express treat ";" as an ordinary character, not a separator.');
  }

  if (raw === "") {
    return { raw, hadQuestionMark, isEmpty: true, params: [], duplicateKeys: [], notes };
  }

  const params = raw.split("&").map((segment, i) => parseSegment(segment, i + 1));

  const seen = new Map<string, number>();
  for (const p of params) {
    if (p.rawSegment === "") continue;
    seen.set(p.key, (seen.get(p.key) ?? 0) + 1);
  }
  const duplicateKeys = [...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k);

  if (duplicateKeys.length > 0) {
    notes.push(
      `Duplicate key${duplicateKeys.length > 1 ? "s" : ""}: ${duplicateKeys.join(", ")}. Every occurrence is listed below, in arrival order.`,
    );
  }
  if (params.some((p) => p.rawSegment === "")) {
    notes.push('Empty segment(s) present -- caused by "&&", or a leading/trailing "&".');
  }

  return { raw, hadQuestionMark, isEmpty: false, params, duplicateKeys, notes };
}

function parseSegment(segment: string, position: number): ParsedParam {
  const segmentNotes: string[] = [];

  if (segment === "") {
    return {
      position,
      rawSegment: "",
      rawKey: "",
      rawValue: null,
      key: "",
      keyDecodeError: null,
      value: null,
      valueDecodeError: null,
      notes: ['Empty segment (from "&&", or a leading/trailing "&").'],
    };
  }

  // Split on the FIRST "=" only: in "a=b=c" everything after the first "=" is the value.
  const eq = segment.indexOf("=");
  const rawKey = eq === -1 ? segment : segment.slice(0, eq);
  const rawValue = eq === -1 ? null : segment.slice(eq + 1);

  if (eq === -1) {
    segmentNotes.push('No "=" in this segment -- the key was sent as a bare flag with no value.');
  } else if (rawValue === "") {
    segmentNotes.push('Value is the empty string (the "=" is present but nothing follows it).');
  }
  if (rawValue !== null && rawValue.includes("=")) {
    segmentNotes.push('Value contains "=" characters; only the first "=" was treated as the separator.');
  }
  if (rawKey === "" && eq !== -1) {
    segmentNotes.push('Key is empty (the segment starts with "=").');
  }
  if (segment.includes("+")) {
    segmentNotes.push('Contains "+", decoded as a space. A literal plus would have arrived as "%2B".');
  }

  const decodedKey = safeDecode(rawKey);
  const decodedValue = rawValue === null ? null : safeDecode(rawValue);

  if (decodedKey.error) segmentNotes.push(`Key could not be decoded: ${decodedKey.error}`);
  if (decodedValue?.error) segmentNotes.push(`Value could not be decoded: ${decodedValue.error}`);

  return {
    position,
    rawSegment: segment,
    rawKey,
    rawValue,
    key: decodedKey.text,
    keyDecodeError: decodedKey.error,
    value: decodedValue === null ? null : decodedValue.text,
    valueDecodeError: decodedValue?.error ?? null,
    notes: segmentNotes,
  };
}

export interface UrlParts {
  path: string;
  /** Query string WITHOUT the leading "?"; null when the URL carried no "?" at all. */
  query: string | null;
  /** Fragment WITHOUT the leading "#"; null when absent. Never populated for server requests. */
  fragment: string | null;
}

/**
 * Split a request target ("/foo?a=1") into parts by hand. `new URL()` normalises and re-encodes,
 * which would destroy the very malformation we are trying to display.
 */
export function splitUrlParts(url: string | null | undefined): UrlParts {
  const source = typeof url === "string" ? url : "";

  const hashAt = source.indexOf("#");
  const beforeHash = hashAt === -1 ? source : source.slice(0, hashAt);
  const fragment = hashAt === -1 ? null : source.slice(hashAt + 1);

  const qAt = beforeHash.indexOf("?");
  const path = qAt === -1 ? beforeHash : beforeHash.slice(0, qAt);
  const query = qAt === -1 ? null : beforeHash.slice(qAt + 1);

  return { path, query, fragment };
}
